import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BarChart2,
  Briefcase,
  DollarSign,
  TrendingUp,
  CheckCircle,
  Clock,
  Download,
  ChevronDown,
  Monitor,
  Apple,
  Terminal,
  Smartphone,
} from 'lucide-react';
import { Container } from '../components/ui/Container';
import { TindisaPartnerLink } from '../components/ui/TindisaPartnerLink';
import { cn } from '../utils/cn';

interface AppEntry {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  category: string;
  status: 'available' | 'coming-soon';
}

// Apps with a dedicated /applications/:id detail page
const DETAIL_PAGE_IDS = [
  'accounting', 'business', 'wallet', 'portfolio', 'risk',
];

const allApps: AppEntry[] = [
  // ── PME & Coopératives ───────────────────────────────────────
  {
    id: 'accounting',
    name: 'Comptabilité',
    description: 'Grand livre, journaux, états financiers SYSCOHADA, import FEC et conformité Blockchain.',
    icon: BarChart2,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    category: 'PME',
    status: 'available',
  },
  {
    id: 'business',
    name: 'Gestion commerciale',
    description: 'POS mobile & desktop, catalogue, stocks, clients, fournisseurs et assistant IA.',
    icon: Briefcase,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    category: 'PME',
    status: 'available',
  },
  {
    id: 'wallet',
    name: 'Wallet',
    description: 'Coffre-Fort financier Wanzo : Mobile Money, demandes de crédit et leasing.',
    icon: DollarSign,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100',
    category: 'PME',
    status: 'coming-soon',
  },
  // ── Institution Financière ───────────────────────────────────
  {
    id: 'portfolio',
    name: 'Gestion de Portefeuille',
    description: 'Portefeuille de crédits, PAR, reporting COBAC/BCC et scoring ACS 5 piliers.',
    icon: TrendingUp,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    category: 'Institution Financière',
    status: 'available',
  },
  {
    id: 'risk',
    name: 'Prospection & Analyse des Risques',
    description: 'Scoring ML, détection d\'anomalies documentaires et workflow de validation des dossiers.',
    icon: BarChart2,
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    category: 'Institution Financière',
    status: 'coming-soon',
  },
];

const statusBadge = (status: AppEntry['status'], t: (k: string) => string) => {
  if (status === 'available') {
    return (
      <span className="flex items-center gap-1 text-[10px] font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
        <CheckCircle className="w-3 h-3" /> {t('apps.available')}
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-[10px] font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
      <Clock className="w-3 h-3" /> {t('apps.comingSoon')}
    </span>
  );
};

const categories = [...new Set(allApps.map((a) => a.category))];

const APP_VERSION = '1.0.0';

// Wanzo Arena : disponible sur tous les systèmes d'exploitation
const arenaPlatforms = [
  {
    id: 'windows',
    label: 'Windows',
    sublabel: '.exe (Windows 10/11)',
    icon: Monitor,
    href: `https://github.com/jaqcquesndav/Wanzo_Land/releases/download/v${APP_VERSION}/WanzoArena-Setup-${APP_VERSION}.exe`,
  },
  {
    id: 'macos',
    label: 'macOS',
    sublabel: '.dmg (macOS 12+)',
    icon: Apple,
    href: `https://github.com/jaqcquesndav/Wanzo_Land/releases/download/v${APP_VERSION}/WanzoArena-${APP_VERSION}.dmg`,
  },
  {
    id: 'linux',
    label: 'Linux',
    sublabel: '.AppImage / .deb',
    icon: Terminal,
    href: `https://github.com/jaqcquesndav/Wanzo_Land/releases/download/v${APP_VERSION}/WanzoArena-${APP_VERSION}.AppImage`,
  },
  {
    id: 'android',
    label: 'Android',
    sublabel: '.apk / Google Play',
    icon: Smartphone,
    href: '#',
  },
  {
    id: 'ios',
    label: 'iOS',
    sublabel: 'App Store',
    icon: Apple,
    href: '#',
  },
];

export default function AllApplications() {
  const { t } = useTranslation();
  const [arenaOpen, setArenaOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setArenaOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16">
      <Container>
        {/* Breadcrumb */}
        <nav className="mb-8 text-sm text-gray-500 flex items-center gap-2">
          <Link to="/" className="hover:text-primary transition-colors">{t('nav.home')}</Link>
          <span>/</span>
          <span className="text-gray-800 font-medium">{t('nav.applications')}</span>
        </nav>

        {/* Header */}
        <div className="mb-12 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6">
          <div className="max-w-2xl">
            <h1 className="text-3xl font-bold text-gray-900 mb-3">{t('apps.title')}</h1>
            <p className="text-gray-500 text-base leading-relaxed">
              {t('apps.subtitle')}
            </p>
          </div>

          {/* Téléchargements + accès marché partenaire Tindisa */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-end flex-shrink-0">
            {/* Partenariat Tindisa : accès à un vaste marché depuis la messagerie */}
            <TindisaPartnerLink label="Vendez sur" className="self-start sm:self-auto" />
            {/* Wanzo Arena : multi-plateforme */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setArenaOpen((v) => !v)}
                className="flex items-center gap-2 bg-white border border-gray-200 text-gray-700 text-sm font-semibold px-4 py-2.5 rounded-xl shadow-sm hover:border-primary hover:text-primary transition-all"
              >
                <Download className="w-4 h-4" />
                Wanzo Arena
                <ChevronDown className={cn('w-4 h-4 transition-transform', arenaOpen && 'rotate-180')} />
              </button>

              {arenaOpen && (
                <div className="absolute right-0 mt-2 w-60 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-20">
                  <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">v{APP_VERSION}</span>
                    <a
                      href={`https://github.com/jaqcquesndav/Wanzo_Land/releases/tag/v${APP_VERSION}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:underline"
                    >
                      Changelog
                    </a>
                  </div>
                  {arenaPlatforms.map((p) => {
                    const Icon = p.icon;
                    return (
                      <a
                        key={p.id}
                        href={p.href}
                        {...(p.href !== '#' ? { download: true } : {})}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors group"
                        onClick={() => setArenaOpen(false)}
                      >
                        <span className="w-8 h-8 rounded-lg bg-gray-100 group-hover:bg-primary/10 flex items-center justify-center flex-shrink-0 transition-colors">
                          <Icon className="w-4 h-4 text-gray-600 group-hover:text-primary" />
                        </span>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{p.label}</p>
                          <p className="text-xs text-gray-400">{p.sublabel}</p>
                        </div>
                        <Download className="w-3.5 h-3.5 text-gray-400 group-hover:text-primary ml-auto" />
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Apps by category */}
        {categories.map((category) => {
          const apps = allApps.filter((a) => a.category === category);
          return (
            <section key={category} className="mb-12">
              <h2 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4 border-b border-gray-200 pb-2">
                {category}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {apps.map((app) => {
                  const Icon = app.icon;
                  const hasDetail = DETAIL_PAGE_IDS.includes(app.id);
                  const to = `/applications/${app.id}`;

                  const cardContent = (
                    <div
                      className={cn(
                        'flex flex-col gap-3 rounded-xl border bg-white p-5 shadow-sm transition-all duration-200 h-full',
                        hasDetail
                          ? 'hover:shadow-md hover:-translate-y-0.5 cursor-pointer border-gray-200 hover:border-primary/30'
                          : 'cursor-default opacity-50 border-gray-100'
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className={cn('rounded-xl p-2.5 flex-shrink-0', app.bgColor)}>
                          <Icon className={cn('w-5 h-5', app.color)} />
                        </span>
                        {statusBadge(app.status, t)}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 text-sm mb-1">{app.name}</h3>
                        <p className="text-xs text-gray-500 leading-relaxed">{app.description}</p>
                      </div>
                      {hasDetail && (
                        <div className={cn('text-xs font-semibold flex items-center gap-1 mt-1', app.color)}>
                          {t('apps.viewDetails')}
                          <ChevronDown className="w-3 h-3 -rotate-90" />
                        </div>
                      )}
                    </div>
                  );

                  if (hasDetail) {
                    return <Link key={app.id} to={to} className="flex">{cardContent}</Link>;
                  }
                  return <div key={app.id}>{cardContent}</div>;
                })}
              </div>
            </section>
          );
        })}
      </Container>
    </div>
  );
}
